import React, { useEffect, useState } from "react";
import styles from "./SpotlightOverlay.module.scss";

/**
 * Full-viewport dim with a rounded-rectangle cutout + pulsing halo.
 *
 * The SVG mask is the same technique v1 used — what changed:
 *   • The cutout animates between rects via CSS transitions on the mask
 *     rect's x/y/width/height attributes. SVG attribute transitions work
 *     in all modern browsers.
 *   • A pulsing border div sits on top of the hole, absolutely positioned
 *     to match the rect. Scales 1 → 1.08 with opacity fade on a 1.6s loop.
 *   • When `rect` is null (no target on this step), we show a plain dim —
 *     the tour widget stays usable.
 */
const PADDING = 10;
const RADIUS = 14;
const DIM_COLOR = "rgba(23, 23, 40, 0.68)";

const SpotlightOverlay = ({ rect, onClick }) => {
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { w: W, h: H } = viewport;

  if (!rect) {
    return <div className={styles.overlay} onClick={onClick} />;
  }

  const x = Math.max(0, rect.x - PADDING);
  const y = Math.max(0, rect.y - PADDING);
  const w = rect.width + PADDING * 2;
  const h = rect.height + PADDING * 2;

  return (
    <div className={styles.root}>
      <svg width={W} height={H} className={styles.svg}>
        <defs>
          <mask id="spot-mask">
            <rect x="0" y="0" width={W} height={H} fill="white" />
            {/*
              Transition the rect between steps — SVG attribute animations
              via CSS `transition` property applied to the rect selector.
            */}
            <rect
              className={styles.maskHole}
              x={x}
              y={y}
              width={w}
              height={h}
              rx={RADIUS}
              ry={RADIUS}
              fill="black"
            />
          </mask>
        </defs>
        <rect x="0" y="0" width={W} height={H} fill={DIM_COLOR} mask="url(#spot-mask)" />
      </svg>

      {/* Pulsing halo — a rounded border that scales + fades. Positioned
          absolutely at the same rect the hole is cut out of. */}
      <div
        className={styles.halo}
        style={{
          left: x,
          top: y,
          width: w,
          height: h,
          borderRadius: RADIUS,
        }}
      />
      <div
        className={styles.haloOuter}
        style={{
          left: x,
          top: y,
          width: w,
          height: h,
          borderRadius: RADIUS,
        }}
      />

      {/* Click regions outside the hole advance the tour. */}
      <div className={styles.tapTop}    style={{ height: y }} onClick={onClick} />
      <div className={styles.tapBottom} style={{ top: y + h }} onClick={onClick} />
      <div className={styles.tapLeft}   style={{ top: y, height: h, width: x }} onClick={onClick} />
      <div className={styles.tapRight}  style={{ top: y, height: h, left: x + w }} onClick={onClick} />
    </div>
  );
};

export default SpotlightOverlay;
